import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Plus, Pencil, Trash2, X } from "lucide-react";

/**
 * Reusable admin CRUD manager.
 * fields: [{ name, label, type: text|textarea|number|select|switch|image, options?, placeholder? }]
 */
export default function ResourceManager({ title, endpoint, fields, columns, defaultItem = {} }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // object or null
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);

  const load = () => api.get(`/admin/${endpoint}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const openNew = () => { setForm({ ...defaultItem }); setEditing({}); };
  const openEdit = (it) => { setForm({ ...it }); setEditing(it); };
  const close = () => setEditing(null);

  const save = async () => {
    setLoading(true);
    try {
      if (editing && editing.id) await api.put(`/admin/${endpoint}/${editing.id}`, { data: form });
      else await api.post(`/admin/${endpoint}`, { data: form });
      toast.success("Enregistré");
      close(); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const remove = async (it) => {
    if (!window.confirm("Supprimer cet élément ?")) return;
    try { await api.delete(`/admin/${endpoint}/${it.id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid={`admin-${endpoint}`}>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display font-black text-3xl">{title}</h1>
        <button onClick={openNew} data-testid={`${endpoint}-new-btn`} className="btn-gold !py-2.5"><Plus size={16} /> Ajouter</button>
      </div>

      <div className="card-dark overflow-x-auto">
        {items.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">Aucun élément. Cliquez sur « Ajouter ».</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500 border-b border-white/10">
              <tr>{columns.map((c) => <th key={c.key} className="p-4">{c.label}</th>)}<th className="p-4 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-white/5">
                  {columns.map((c) => (
                    <td key={c.key} className="p-4 text-slate-300">
                      {c.render ? c.render(it) : (typeof it[c.key] === "boolean" ? (it[c.key] ? "Oui" : "Non") : String(it[c.key] ?? "—")).slice(0, 60)}
                    </td>
                  ))}
                  <td className="p-4 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(it)} data-testid={`${endpoint}-edit-${it.id}`} className="text-slate-400 hover:text-gold p-1.5"><Pencil size={16} /></button>
                    <button onClick={() => remove(it)} data-testid={`${endpoint}-delete-${it.id}`} className="text-slate-400 hover:text-red-400 p-1.5"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={close} />
          <div className="relative card-dark w-full max-w-2xl max-h-[88vh] overflow-y-auto p-8" data-testid={`${endpoint}-form-modal`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display font-bold text-xl">{editing.id ? "Modifier" : "Nouveau"}</h2>
              <button onClick={close} className="text-slate-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {fields.map((f) => (
                <div key={f.name} className={f.type === "textarea" || f.full ? "sm:col-span-2" : ""}>
                  <label className="label-dark">{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea rows={f.rows || 4} className="input-dark" data-testid={`field-${f.name}`} value={form[f.name] ?? ""} onChange={(e) => setF(f.name, e.target.value)} placeholder={f.placeholder} />
                  ) : f.type === "select" ? (
                    <select className="input-dark" data-testid={`field-${f.name}`} value={form[f.name] ?? f.options[0]} onChange={(e) => setF(f.name, e.target.value)}>
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : f.type === "switch" ? (
                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <input type="checkbox" data-testid={`field-${f.name}`} className="accent-amber-400 w-5 h-5" checked={!!form[f.name]} onChange={(e) => setF(f.name, e.target.checked)} />
                      <span className="text-sm text-slate-400">{form[f.name] ? "Actif" : "Inactif"}</span>
                    </label>
                  ) : f.type === "number" ? (
                    <input type="number" step="any" className="input-dark" data-testid={`field-${f.name}`} value={form[f.name] ?? ""} onChange={(e) => setF(f.name, e.target.value === "" ? "" : Number(e.target.value))} placeholder={f.placeholder} />
                  ) : (
                    <input className="input-dark" data-testid={`field-${f.name}`} value={form[f.name] ?? ""} onChange={(e) => setF(f.name, e.target.value)} placeholder={f.placeholder} />
                  )}
                  {f.type === "image" && form[f.name] && <img src={form[f.name]} alt="" className="mt-2 h-20 rounded object-cover" />}
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={save} disabled={loading} data-testid={`${endpoint}-save-btn`} className="btn-gold">{loading ? "…" : "Enregistrer"}</button>
              <button onClick={close} className="btn-ghost">Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
